import type { VoterDataFieldDto, VoterDataOptionDto } from '@signalvote/shared';

/**
 * Search over the field catalog for the sentence's last blank. A field matches by its label,
 * category or synonyms; one of its values matches by its label, how it reads in the sentence
 * ("women") or its own synonyms ("teachers" → Educator). Results come back grouped by field, best
 * first. Values L2 can only count, never filter on, are never offered.
 */

export interface FieldSearchGroup {
  field: VoterDataFieldDto;
  /** The values that matched, in catalog order. */
  values: VoterDataOptionDto[];
  /**
   * The value Enter chooses, or null when it opens the field. A value is the default only when the
   * text names it, by its label or synonyms, at least as closely as it names the field: "teachers"
   * picks Educator, but "ethnicity" opens Ethnicity rather than "another ethnicity".
   */
  pick: VoterDataOptionDto | null;
}

const MAX_GROUPS = 8;

/** How closely a text matched: a label beats a synonym beats a category. Lower is better. */
enum Closeness {
  Label = 0,
  Synonym = 1,
  Category = 2,
}

/** "Teachers" and "teacher" are the same search. */
const stem = (word: string) => (word.length > 3 && word.endsWith('s') && !word.endsWith('ss') ? word.slice(0, -1) : word);

function wordsOf(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[’']/g, '')
    .split(/[^a-z0-9$+]+/)
    .filter(Boolean)
    .map(stem);
}

/** Every word typed starts some word of the text, so "teach" finds "Teachers" and "gun own" finds "gun owners". */
function matches(text: string | null | undefined, query: string[]): boolean {
  if (!text) return false;
  const words = wordsOf(text);
  return query.every((q) => words.some((w) => w.startsWith(q)));
}

function closeness(query: string[], byCloseness: [Closeness, (string | null | undefined)[]][]): Closeness | null {
  for (const [rank, texts] of byCloseness) if (texts.some((t) => matches(t, query))) return rank;
  return null;
}

export function searchFields(fields: VoterDataFieldDto[], text: string): FieldSearchGroup[] {
  const query = wordsOf(text);
  if (query.length === 0) return [];

  const ranked: { group: FieldSearchGroup; rank: Closeness }[] = [];
  for (const field of fields) {
    const fieldRank = closeness(query, [
      [Closeness.Label, [field.label]],
      [Closeness.Synonym, field.synonyms],
      [Closeness.Category, [field.category]],
    ]);
    const valueRanks = field.options
      .filter((o) => o.selectable)
      .map((option) => {
        const own = closeness(query, [[Closeness.Label, [option.label]], [Closeness.Synonym, option.synonyms]]);
        return { option, own, rank: own ?? closeness(query, [[Closeness.Label, [option.phrase]]]) };
      })
      .filter((v): v is { option: VoterDataOptionDto; own: Closeness | null; rank: Closeness } => v.rank !== null);
    if (fieldRank === null && valueRanks.length === 0) continue;

    const named = valueRanks
      .filter((v) => v.own !== null && (fieldRank === null || v.own <= fieldRank))
      .sort((a, b) => a.own! - b.own!)[0];
    const pick = named?.option ?? (fieldRank === null ? valueRanks[0].option : null);
    const rank = Math.min(fieldRank ?? Infinity, ...valueRanks.map((v) => v.rank));
    ranked.push({ group: { field, values: valueRanks.map((v) => v.option), pick }, rank });
  }

  // Stable, so equally close fields keep catalog order.
  return ranked
    .sort((a, b) => a.rank - b.rank)
    .slice(0, MAX_GROUPS)
    .map((r) => r.group);
}
